import { useCallback } from 'react'
import type { LazyQueryHookOptions, LazyQueryResultTuple, OperationVariables } from '@apollo/client'

import type { CaseSearchResultItem, GetCaseSearchResultsQueryResult, GetCaseSearchResultsQueryVariables, useGetCaseSearchResultsLazyQuery } from '@acre/graphql'

export type UseGlobalCaseSearchProps<
	Query extends UseQueryHook,
	Variables extends OperationVariables = OperationVariables,
	Data = ReturnType<Query>,
> = {
	query: Query
	initialQueryVariables?: Variables
	getData?: (response: Data) => any[]
}

export type UseQueryHook<Data extends any = any, Variables extends OperationVariables = any> = (
	baseOptions: LazyQueryHookOptions<Data, Variables>,
) => LazyQueryResultTuple<Data, Variables>

/**
 * Custom hook for performing global case search operations in combination with AutocompleteField
 * Manages case search queries, pagination, and data transformation 
 */
const useGlobalCaseSearch = <Data>(
	params: UseGlobalCaseSearchProps<typeof useGetCaseSearchResultsLazyQuery, GetCaseSearchResultsQueryVariables, Data>,
) => {
	const { query: useQueryProp } = params
	/**
	 * Creates query variables object from search parameters
	 */
	const getQueryVariables = useCallback((data: { value?: string | number; cursor?: number }): {
		variables: GetCaseSearchResultsQueryVariables
	} => {
		const { value, cursor } = data
		return {
			variables: {
				searchFragment: encodeURIComponent(value ?? ''),
				bookmark: cursor ?? 0,
			}
		}
	}, [])

	const [getCaseSearchResults, { loading, fetchMore, networkStatus }] = useQueryProp({
		notifyOnNetworkStatusChange: true,
		fetchPolicy: 'no-cache',
	})

	const loadingStatus = loading || networkStatus === 4 /* refetch */ || networkStatus === 1 /* loading */

	/**
	 * Extracts the pagination cursor from response data
	 * Return null if there's no bookmark or we've reached the end
	 */
	const getPageCursor = useCallback((data: any): number | null => {
		const bookmark = data?.data?.caseSearch?.bookmark
		if (!bookmark || bookmark === data?.data?.caseSearch?.total_results) return null

		return bookmark
	}, [])

	/**
	 * Extracts search result items from the response data 
	 */
	const getOptions = useCallback((options: GetCaseSearchResultsQueryResult): CaseSearchResultItem[] => {
		return (options?.data?.caseSearch?.results ?? []) as CaseSearchResultItem[]
	}, [])

	/**
	 * Loads more search results for pagination
	 */
	const loadMore = useCallback(async (variables: { variables: GetCaseSearchResultsQueryVariables }) => {
		const result = await fetchMore({
			variables: variables.variables,
			// we are using a `no-cache` fetch policy so we need to tell apollo how to merge the results
			updateQuery: (prev, { fetchMoreResult }) => {
				if (!fetchMoreResult) return prev;

				return {
					...prev,
					caseSearch: {
						...prev.caseSearch,
						results: [...prev.caseSearch?.results ?? [], ...fetchMoreResult.caseSearch?.results ?? []]
					}
				}
			}
		});

		return result;
	}, [fetchMore]);

	return {
		loading: loadingStatus,
		loadData: getCaseSearchResults,
		loadMore,
		getQueryVariables,
		getPageCursor,
		getOptions,
	}
}

export default useGlobalCaseSearch
